import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink, Paperclip, Banknote } from "lucide-react";
import { ProviderLogo } from "@/components/ProviderLogo";
import { getXeroStatus, syncInvoiceToXero, pushXeroPayment } from "@/lib/apiClient";

const payStateOf = (inv) => {
  const x = inv.xero;
  if (!x || !inv.payment || inv.payment.method === "xero") return null;
  return x.payment_pushed ? "done" : "pending";
};

const PaymentRow = ({ inv, onSynced }) => {
  const [busy, setBusy] = useState(false);
  const x = inv.xero;
  const state = payStateOf(inv);
  const justPaid = inv.payment?.marked_at && Date.now() - new Date(inv.payment.marked_at).getTime() < 60000;
  useEffect(() => {
    if (state !== "pending" || !justPaid || x.payment_error) return undefined;
    const t = setTimeout(() => onSynced?.(), 4000); // the background push usually lands within a few seconds
    return () => clearTimeout(t);
  }, [state, justPaid, x?.payment_error, onSynced]);
  if (!state) return null;
  const push = () => {
    setBusy(true);
    pushXeroPayment(inv.id).then((r) => { if (r.payment_id != null) toast.success(`Payment recorded on the Xero bill${r.authorised ? " (bill approved in Xero)" : ""}`); else toast.message(r.skipped || "Nothing to record"); onSynced?.(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not record payment in Xero", { duration: 8000 })).finally(() => setBusy(false));
  };
  if (state === "done") {
    return <p className="mt-2 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid="xero-payment-pushed"><Banknote className="h-3 w-3" /> {x.payment_pushed === "already_paid" ? "Already paid in Xero" : `Payment recorded on the Xero bill${x.payment_account ? ` from ${x.payment_account}` : ""}`}</p>;
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="xero-payment-pending">
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={push} disabled={busy} data-testid="xero-push-payment-btn">{busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Banknote className="h-3 w-3 mr-1" />} Record payment in Xero</Button>
      {x.payment_error ? <span className="text-[11px] text-amber-500" data-testid="xero-payment-error">Last attempt: {x.payment_error}</span>
        : <span className="text-[11px] text-muted-foreground">Paid in D20 — not yet recorded on the Xero bill</span>}
    </div>
  );
};

export const XeroSyncButton = ({ inv, user, onSynced }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getXeroStatus().then(setSt).catch(() => setSt(null)); }, []);
  if (!st || (!st.connected && st.status !== "reauth_required" && !inv.xero)) return null;
  const x = inv.xero;
  const offline = !st.connected;
  const sync = () => {
    setBusy(true);
    syncInvoiceToXero(inv.id, user).then((r) => { toast.success(r.adopted ? `Linked to the existing Xero bill (status ${r.xero.status})${r.xero.status === "PAID" ? " — marked paid in D20" : ""}` : `${r.xero.updated ? "Updated" : "Created"} draft bill in Xero (${r.xero.tenant_name})${r.xero.converted ? ` — posted in ${r.xero.converted.to} at ${r.xero.converted.rate}` : ""}`); onSynced?.(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Xero sync failed", { duration: 8000 })).finally(() => setBusy(false));
  };
  return (
    <div className="mb-3 rounded-lg border border-sky-500/30 bg-sky-500/5 p-3" data-testid="xero-sync-card">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={sync} disabled={busy || inv.status !== "approved" || offline} title={offline ? "Reconnect Xero in Settings first" : undefined} data-testid="xero-sync-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ProviderLogo provider="xero" className="mr-1.5 bg-white rounded-full" />}{x ? "Re-sync to Xero" : "Sync to Xero"}
        </Button>
        {x ? (
          <span className="text-xs text-muted-foreground flex items-center gap-2" data-testid="xero-sync-status">
            {x.adopted ? "Linked to Xero bill" : "Draft bill"} in {x.tenant_name} · {new Date(x.synced_at).toLocaleString()}{x.attached && <Paperclip className="h-3 w-3" title="PDF attached" />}{x.tracked && <span className="rounded bg-sky-500/15 px-1 py-0.5 text-[10px] text-sky-500" data-testid="xero-tracked-tag">tracking sent</span>}
            {x.converted && <span className="text-amber-500" data-testid="xero-converted-note">posted in {x.converted.to} ({x.converted.from} {Number(x.converted.original_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} @ {x.converted.rate})</span>}
            <a href={x.url} target="_blank" rel="noreferrer" className="text-sky-500 inline-flex items-center gap-1 hover:underline" data-testid="xero-open-link">Open in Xero <ExternalLink className="h-3 w-3" /></a>
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{inv.status === "approved" ? `Creates a draft bill in ${st.tenant_name} with the PDF attached` : "Approve first"}</span>
        )}
        {x?.status && x.status !== "DRAFT" && <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-muted" data-testid="xero-bill-status">{x.status} in Xero</span>}
      </div>
      {!offline && <PaymentRow inv={inv} onSynced={onSynced} />}
      {offline && <p className="mt-2 text-xs text-amber-500" data-testid="xero-offline-note">Xero connection needs re-authorising — <Link to="/settings?tab=xero" className="underline">reconnect in Settings</Link> to sync again.</p>}
      {inv.xero_error && !x && <p className="mt-2 text-xs text-amber-500" data-testid="xero-sync-error">Last sync failed {new Date(inv.xero_error.at).toLocaleString()}: {inv.xero_error.detail}</p>}
    </div>
  );
};
